<script lang="ts">
    import { getModalStore, getToastStore } from "@skeletonlabs/skeleton";
    import { slide } from "svelte/transition";

    import RedX from "$lib/assets/RedX.svg";
    import UserCircle from "$lib/assets/UserCircle.svg";
    import XMark from "$lib/assets/XMark.svg";

    import ProfilePicture from "$lib/components/ProfilePicture.svelte";

    import { users_list } from "$lib/stores";
    import { goto } from "$app/navigation";

    const modalStore = getModalStore();
    const toastStore = getToastStore();

    let user_resp = fetch(
        "/api/drafts/user_permissions?" + new URLSearchParams({ application_id: $modalStore[0].meta?.application_id }),
    )
        .then((resp) => resp.json())
        .then((res) => {
            users_list.set(res);
        });

    let loading: boolean = false;
    let new_user: string = "";
</script>

<div class="bg-white p-8 rounded-lg">
    <div class="flex items-center mb-4">
        <img src={UserCircle} />
        <header class="font-semibold text-xl ml-3">Manage Team</header>
        <img
            class="ml-auto h-8 w-8 hover:bg-stone-200 p-1 transition duration-300 rounded-lg cursor-pointer"
            on:click={() => {
                $modalStore[0].response(undefined); // without this the callback function in modal settings will not trigger
                modalStore.close();
            }}
            src={XMark}
        />
    </div>

    <div class="h-[300px] overflow-y-scroll bg-stone-100 rounded-lg px-2 shadow-md">
        <table class="border-separate border-spacing-y-3 min-w-[500px] mr-2">
            <tbody>
                {#await user_resp}
                    {#each { length: 10 } as i}
                        <tr class="px-4 py-2">
                            <td>
                                <div class="loading_background h-8 rounded-lg" />
                            </td>
                        </tr>
                    {/each}
                {:then}
                    {#each $users_list as user (user.user_id)}
                        <tr class="hover:bg-blue-100 px-4 py-2 transition duration-300">
                            <td transition:slide={{ duration: 300 }} class="pl-2 py-1 rounded-l-lg">
                                <ProfilePicture
                                    user_id={user.user_id}
                                    user_name={user.users.full_name ?? user.users.email}
                                    size={"8"}
                                />
                            </td>
                            <td
                                transition:slide={{ duration: 300 }}
                                class="text-left pl-2 py-1"
                                class:font-bold={user.users.full_name}
                                class:font-extralight={!user.users.full_name}
                                class:italic={!user.users.full_name}>{user.users.full_name ?? "Name Not Set"}</td
                            >
                            <td transition:slide={{ duration: 300 }} class="text-left pl-5 py-1">{user.users.email}</td>
                            <td transition:slide={{ duration: 300 }} class="text-left pl-10 py-1"
                                ><select
                                    value={user.role}
                                    class="capitalize rounded-lg border-none font-light shadow-sm"
                                    disabled={loading ||
                                        $users_list.find((element) => element.user_id == $modalStore[0].meta?.user_id)
                                            ?.role == "user"}
                                    on:change={async (e) => {
                                        loading = true;
                                        const user_index = $users_list.findIndex(
                                            (element) => element.user_id == user.user_id,
                                        );

                                        const resp = await fetch("/api/drafts/user_permissions", {
                                            method: "POST",
                                            headers: {
                                                Accept: "application/json",
                                                "Content-Type": "application/json",
                                            },
                                            body: JSON.stringify({
                                                application_id: $modalStore[0].meta?.application_id,
                                                user_email: user.users.email,
                                                role: e.target?.value,
                                            }),
                                        });

                                        if (!resp.ok) {
                                            e.target.value = $users_list[user_index].role;
                                            try {
                                                const res = await resp.json();
                                                toastStore.trigger({
                                                    message: "✗ " + res.message,
                                                    background: "bg-zinc-800 text-red-400 text-center",
                                                    hideDismiss: true,
                                                    timeout: 8000,
                                                });
                                            } catch (e) {
                                                toastStore.trigger({
                                                    message:
                                                        "✗ It seems like something went wrong. Try again or contact us if the issue persists.",
                                                    background: "bg-zinc-800 text-red-400 text-center",
                                                    hideDismiss: true,
                                                    timeout: 8000,
                                                });
                                            }
                                        } else {
                                            if (e.target.value == "owner") {
                                                const former_owner_index = $users_list.findIndex(
                                                    (element) => element.role == "owner",
                                                );
                                                $users_list[former_owner_index].role = "admin";
                                            }

                                            $users_list[user_index].role = e.target.value;
                                            $users_list = $users_list;

                                            toastStore.trigger({
                                                message: "✓ Role updated!",
                                                background: "bg-zinc-800 text-green-300 text-center",
                                                hideDismiss: true,
                                                timeout: 4000,
                                            });
                                        }

                                        loading = false;
                                    }}
                                >
                                    <option value="owner">owner</option>
                                    <option value="admin">admin</option>
                                    <option value="user">user</option>
                                </select>
                            </td>
                            <td transition:slide={{ duration: 300 }} class="pl-5 pr-2 py-1 rounded-r-lg">
                                <!-- svelte-ignore a11y-click-events-have-key-events -->
                                <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
                                <img
                                    alt="Delete"
                                    src={RedX}
                                    class="hover:bg-red-200 rounded-md cursor-pointer transition duration-300"
                                    height="18px"
                                    width="18px"
                                    on:click={async () => {
                                        if (!loading) {
                                            loading = true;
                                            const resp = await fetch("/api/drafts/user_permissions", {
                                                method: "DELETE",
                                                headers: {
                                                    Accept: "application/json",
                                                    "Content-Type": "application/json",
                                                },
                                                body: JSON.stringify({
                                                    application_id: $modalStore[0].meta?.application_id,
                                                    user_id: user.user_id,
                                                }),
                                            });

                                            if (resp.ok) {
                                                $users_list = $users_list.filter(
                                                    (element) => element.user_id != user.user_id,
                                                );
                                                toastStore.trigger({
                                                    message: "✓ Successfully deleted user!",
                                                    background: "bg-zinc-800 text-green-300 text-center",
                                                    hideDismiss: true,
                                                    timeout: 4000,
                                                });
                                                if (user.user_id == $modalStore[0].meta?.user_id) {
                                                    modalStore.close();
                                                    goto("/workspace");
                                                }
                                            } else {
                                                try {
                                                    const res = await resp.json();
                                                    toastStore.trigger({
                                                        message: "✗ " + res.message,
                                                        background: "bg-zinc-800 text-red-400 text-center",
                                                        hideDismiss: true,
                                                        timeout: 8000,
                                                    });
                                                } catch (e) {
                                                    toastStore.trigger({
                                                        message:
                                                            "✗ It seems like something went wrong. Try again or contact us if the issue persists.",
                                                        background: "bg-zinc-800 text-red-400 text-center",
                                                        hideDismiss: true,
                                                        timeout: 8000,
                                                    });
                                                }
                                            }
                                            loading = false;
                                        }
                                    }}
                                />
                            </td>
                        </tr>
                    {/each}
                {/await}
            </tbody>
        </table>
    </div>
    <form
        on:submit={async (event) => {
            event.preventDefault();

            if (!loading) {
                if ($users_list.find((element) => element.users.email == new_user)) {
                    toastStore.trigger({
                        message: "✗ This user is already present.",
                        background: "bg-zinc-800 text-red-400 text-center",
                        hideDismiss: true,
                        timeout: 4000,
                    });
                    return;
                }
                loading = true;
                const resp = await fetch("/api/drafts/user_permissions", {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        application_id: $modalStore[0].meta?.application_id,
                        user_email: new_user,
                        role: "user",
                    }),
                });

                if (resp.ok) {
                    const res = await resp.json();
                    $users_list.push(res);
                    $users_list = $users_list;
                    toastStore.trigger({
                        message: "✓ Successfully added user!",
                        background: "bg-zinc-800 text-green-300 text-center",
                        hideDismiss: true,
                        timeout: 4000,
                    });
                } else {
                    try {
                        const res = await resp.json();
                        toastStore.trigger({
                            message: "✗ " + res.message,
                            background: "bg-zinc-800 text-red-400 text-center",
                            hideDismiss: true,
                            timeout: 8000,
                        });
                    } catch (e) {
                        toastStore.trigger({
                            message:
                                "✗ It seems like something went wrong. Try again or contact us if the issue persists.",
                            background: "bg-zinc-800 text-red-400 text-center",
                            hideDismiss: true,
                            timeout: 8000,
                        });
                    }
                }

                new_user = "";
                loading = false;
            }
        }}
    >
        <div class="flex mt-6 w-full">
            <input
                class="rounded-lg bg-stone-100 flex-grow"
                type="text"
                id="user_email"
                placeholder="Enter Email..."
                bind:value={new_user}
            />
            <button
                class="bg-zinc-800 text-white rounded-lg py-2 px-4 ml-3 hover:shadow-md transition duration-300"
                class:bg-zinc-800={!loading}
                class:bg-secondary-600={loading}
                >Add User
            </button>
        </div>
    </form>
</div>

<style>
    @keyframes moving-gradient {
        0% {
            background-position: -250px 0;
        }
        100% {
            background-position: 250px 0;
        }
    }

    div.loading_background {
        background: linear-gradient(to right, #eee 20%, #ddd 50%, #eee 80%);
        background-size: 500px 100px;
        animation-name: moving-gradient;
        animation-duration: 1s;
        animation-iteration-count: infinite;
        animation-timing-function: linear;
        animation-fill-mode: forwards;
    }
</style>
